import {
  OWN_AVATAR_STYLE_LABEL,
  OWN_AVATAR_STYLES_OFFERED,
  PLAN_CATALOG,
  type CharacterKey,
  type OwnAvatarStyle,
} from '@secretario/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  AVATAR_KEY,
  confirmAvatar,
  deleteAvatar,
  getAvatarStatus,
  previewAvatar,
  shrinkPhoto,
} from '../../lib/api/avatars.js';
import { ApiError, describeError } from '../../lib/api/http.js';
import { characterImage } from '../../lib/characters.js';
import { hapticSuccess, hapticTap } from '../../lib/native.js';
import { openPaywall } from '../../lib/plan.js';
import { ConsentCheck, DocLink } from '../legal/Consent.js';
import './own-avatar.css';

/** Cada estilo mostra uma amostra do material, não um rosto de outra pessoa. */
const STYLE_SAMPLE: Record<(typeof OWN_AVATAR_STYLES_OFFERED)[number], string> = {
  massinha: '/styles/massinha/sol.webp',
  ilustracao: '/styles/editorial/sol.webp',
};

const WORKING_STEPS = ['Olhando a sua foto', 'Desenhando você', 'Dando os retoques'];

/**
 * "Crie o seu": a pessoa escolhe o estilo, tira ou escolhe uma selfie, aceita
 * o envio ao Google e recebe uma versão. Gostou, usa; senão, pede outra.
 * A foto não fica guardada; o desenho vira o avatar do Secretário.
 */
export function OwnAvatarPanel({
  selected,
  onUse,
  onPreview,
  onRemoved,
}: {
  /** O avatar próprio é o que está escolhido agora. */
  selected: boolean;
  onUse: (key: CharacterKey) => void;
  /** Mostra a versão no palco da tela (ou volta ao normal com null). */
  onPreview: (image: string | null) => void;
  onRemoved: () => void;
}) {
  const queryClient = useQueryClient();
  const statusQ = useQuery({
    queryKey: AVATAR_KEY,
    queryFn: getAvatarStatus,
    staleTime: 20_000,
    // Enquanto as expressões são desenhadas, confere de tempos em tempos.
    refetchInterval: (query) => {
      const drawn = query.state.data?.avatar?.expressions.length;
      return drawn !== undefined && drawn < 6 ? 8_000 : false;
    },
  });
  const avatar = statusQ.data?.avatar ?? null;
  const quota = statusQ.data?.quota;

  const [creating, setCreating] = useState(false);
  const [style, setStyle] = useState<OwnAvatarStyle>('massinha');
  const [photo, setPhoto] = useState<{ base64: string; url: string } | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState<{ id: string; image: string } | null>(null);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);

  const building = creating || !avatar;

  useEffect(() => {
    if (!busy) return;
    setStep(0);
    const timer = window.setInterval(
      () => setStep((current) => Math.min(current + 1, WORKING_STEPS.length - 1)),
      6500,
    );
    return () => window.clearInterval(timer);
  }, [busy]);

  useEffect(() => () => onPreview(null), [onPreview]);

  async function choose(file: File | undefined) {
    if (!file) return;
    setError('');
    try {
      const base64 = await shrinkPhoto(file);
      setPhoto({ base64, url: `data:image/jpeg;base64,${base64}` });
      hapticTap();
    } catch {
      setError('Não consegui abrir essa foto. Tenta outra?');
    }
  }

  function handleError(failure: unknown) {
    if (failure instanceof ApiError && failure.status === 402) {
      openPaywall('avatar', 'crie_o_seu');
      return;
    }
    setError(describeError(failure));
  }

  async function draw() {
    if (!photo || !consent || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await previewAvatar({ style, photo: photo.base64, mime: 'image/jpeg' });
      setPreview({ id: result.previewId, image: result.image });
      onPreview(result.image);
      queryClient.setQueryData(AVATAR_KEY, (current: typeof statusQ.data) =>
        current ? { ...current, quota: result.quota } : current,
      );
      hapticSuccess();
    } catch (failure) {
      handleError(failure);
    } finally {
      setBusy(false);
    }
  }

  async function use() {
    if (!preview || busy) return;
    setBusy(true);
    setError('');
    try {
      const saved = await confirmAvatar(preview.id);
      await queryClient.invalidateQueries({ queryKey: AVATAR_KEY });
      setPreview(null);
      setPhoto(null);
      setConsent(false);
      setCreating(false);
      onPreview(null);
      onUse(saved.characterKey);
      hapticSuccess();
    } catch (failure) {
      handleError(failure);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await deleteAvatar();
      await queryClient.invalidateQueries({ queryKey: AVATAR_KEY });
      setConfirmDelete(false);
      onRemoved();
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  if (statusQ.isLoading) return <div className="oa-loading" aria-label="Carregando" />;

  // Já tem avatar e não está fazendo outro: escolher, refazer ou apagar.
  if (!building) {
    const ready = avatar.expressions.length;
    return (
      <div className="oa">
        <button
          type="button"
          className={`oa-mine${selected ? ' on' : ''}`}
          onClick={() => onUse(avatar.characterKey)}
        >
          <span className="oa-mine-art">
            <img src={characterImage(avatar.characterKey, 'simpatico')} alt="Seu avatar" />
          </span>
          <span className="oa-mine-txt">
            <b>{selected ? 'Seu avatar está em uso' : 'Usar o meu avatar'}</b>
            <small>
              {OWN_AVATAR_STYLE_LABEL[avatar.style]}
              {ready < 6 ? ` · desenhando as expressões (${ready} de 6)` : ' · 6 expressões'}
            </small>
          </span>
        </button>
        <div className="oa-actions">
          <button
            type="button"
            className="oa-ghost"
            onClick={() =>
              quota?.canPreview ? setCreating(true) : openPaywall('avatar', 'crie_o_seu_refazer')
            }
          >
            Fazer outro
            {quota && !quota.canPreview && (
              <i className="oa-pro">
                {PLAN_CATALOG.basic.name} e {PLAN_CATALOG.pro.name}
              </i>
            )}
          </button>
          {confirmDelete ? (
            <span className="oa-confirm">
              <span>Apagar de vez?</span>
              <button
                type="button"
                className="oa-danger"
                disabled={busy}
                onClick={() => void remove()}
              >
                Apagar
              </button>
              <button type="button" className="oa-ghost" onClick={() => setConfirmDelete(false)}>
                Manter
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="oa-ghost subtle"
              onClick={() => setConfirmDelete(true)}
            >
              Apagar meu avatar
            </button>
          )}
        </div>
        {error && <p className="oa-error">{error}</p>}
      </div>
    );
  }

  // Versão pronta: usar ou pedir outra.
  if (preview) {
    return (
      <div className="oa">
        <p className="oa-lead">Ficou assim. Gostou?</p>
        <div className="oa-actions">
          <button type="button" className="oa-cta" disabled={busy} onClick={() => void use()}>
            {busy ? 'Guardando…' : 'Usar este'}
          </button>
          <button
            type="button"
            className="oa-ghost"
            disabled={busy || !quota?.canPreview}
            onClick={() => {
              setPreview(null);
              onPreview(null);
              void draw();
            }}
          >
            Desenhar de novo
          </button>
        </div>
        <p className="oa-note">
          {quota?.canPreview
            ? quota.previewsLeft === 1
              ? 'Você ainda pode pedir mais 1 versão.'
              : `Você ainda pode pedir mais ${quota.previewsLeft} versões.`
            : 'Essa foi a última versão por agora.'}{' '}
          Depois de escolher, eu desenho as outras 5 expressões.
        </p>
        {error && <p className="oa-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="oa">
      <p className="oa-lead">
        Um Secretário com a sua cara. Você manda uma selfie e eu desenho no estilo que escolher.
      </p>

      <div className="oa-styles" role="radiogroup" aria-label="Estilo do desenho">
        {OWN_AVATAR_STYLES_OFFERED.map((id) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={style === id}
            className={style === id ? 'on' : ''}
            onClick={() => setStyle(id)}
          >
            <img src={STYLE_SAMPLE[id]} alt="" loading="lazy" />
            {OWN_AVATAR_STYLE_LABEL[id]}
          </button>
        ))}
      </div>

      <div className="oa-photo">
        {photo ? (
          <img className="oa-selfie" src={photo.url} alt="Sua foto" />
        ) : (
          <span className="oa-selfie empty" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30">
              <circle cx="12" cy="9" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M4.5 20c1.4-3.6 4.2-5.4 7.5-5.4s6.1 1.8 7.5 5.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </span>
        )}
        <div className="oa-photo-btns">
          <button type="button" className="oa-ghost" onClick={() => cameraRef.current?.click()}>
            Tirar selfie
          </button>
          <button type="button" className="oa-ghost" onClick={() => galleryRef.current?.click()}>
            Escolher da galeria
          </button>
          <small>De frente, com o rosto bem visível e boa luz.</small>
        </div>
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="user"
          hidden
          onChange={(event) => {
            void choose(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            void choose(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </div>

      {photo && (
        <ConsentCheck
          checked={consent}
          onChange={setConsent}
          details={
            <p>
              A foto vai ao Google (Gemini) só para fazer o desenho. O app não guarda a foto; o
              Google não usa para treinar inteligência artificial e só a mantém pelo prazo curto de
              controle de abuso dos termos dele. Fica o avatar desenhado, que você apaga quando
              quiser. Mais na <DocLink doc="privacidade">Política de Privacidade</DocLink>.
            </p>
          }
        >
          Concordo com o envio desta foto ao Google para criar o avatar. O app não guarda a foto.
        </ConsentCheck>
      )}

      {busy ? (
        <div className="oa-working" role="status">
          <span className="oa-working-bar" aria-hidden="true">
            <i />
          </span>
          <b>{WORKING_STEPS[step]}</b>
          <small>Leva uns 20 segundos.</small>
        </div>
      ) : (
        <button
          type="button"
          className="oa-cta"
          disabled={!photo || !consent || quota?.canPreview === false}
          onClick={() => void draw()}
        >
          Criar meu avatar
        </button>
      )}
      {quota && !quota.canPreview && !busy && (
        <p className="oa-note">
          {quota.needsPlan ? (
            <>
              O primeiro avatar já foi.{' '}
              <button
                type="button"
                className="oa-link"
                onClick={() => openPaywall('avatar', 'crie_o_seu_limite')}
              >
                Fazer outro com o {PLAN_CATALOG.basic.name} ou o {PLAN_CATALOG.pro.name}
              </button>
            </>
          ) : (
            'Você chegou ao limite deste mês. No mês que vem tem mais.'
          )}
        </p>
      )}
      {quota?.canPreview && !busy && !avatar && (
        <p className="oa-note">O primeiro avatar é por nossa conta.</p>
      )}
      {avatar && !busy && (
        <button type="button" className="oa-link" onClick={() => setCreating(false)}>
          Voltar para o meu avatar
        </button>
      )}
      {error && <p className="oa-error">{error}</p>}
    </div>
  );
}
